import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Col, Form, Input, Row, Select, Space, Typography, Modal, Button, message, Popconfirm, Divider, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import './styles.css';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { useCampaignSaveManager } from './hooks/useCampaignSaveManager';
import { HasAccess } from '@permify/react-role';
import { formatCampaignName } from '../../../core/utils/campaignNaming';
import { useTimezone } from '../../../core/hooks/useTimezone';
import { getCpmForCampaignType } from '../../../core/utils/pricing/cpmHelper';

const { Text } = Typography;

const CampaignRequestorDetails = ({ formValues, helpers }) => {
  const {
    childrenOrg,
    loadingChildrenOrgs,
    currentUserOrg,
    selectedAdvertiser,
    fetchChildrenOrgs,
    currentUser,
    currentCampaignOrder,
    updateCampaignOrder,
    updateCampaignOrderSalesforceId,
    createSalesforceOpportunity,
    loadingCreateSalesforceOpportunity,
    createSalesforceOpportunityLineItems,
    loadingCreateSalesforceOpportunityLineItems,
    createSalesforceContact,
    createSalesforceOpportunityContactRole,
    fetchSalesforceOpportunityProposalNumber,
    salesforceOpportunityProposalNumber,
    loadingSalesforceOpportunityProposalNumber,
  } = useCampaignRequestorContext();

  const [advertisers, setAdvertisers] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newIONumber, setNewIONumber] = useState('');
  const [ioSearchText, setIOSearchText] = useState('');
  const history = useHistory();
  const { saveCampaign } = useCampaignSaveManager();
  const { timezone } = useTimezone();

  // ─── Create Salesforce Opportunity modal state ──────────────────────────
  const [isSfOppModalVisible, setIsSfOppModalVisible] = useState(false);
  const [sfOppName, setSfOppName] = useState('');
  const [sfBudget, setSfBudget] = useState('');
  const [sfMonths, setSfMonths] = useState('');
  // const [sfDecisionMakerFirstName, setSfDecisionMakerFirstName] = useState('');
  // const [sfDecisionMakerLastName, setSfDecisionMakerLastName] = useState('');
  // const [sfFourthField, setSfFourthField] = useState('');
  const [isCreatingSfOpp, setIsCreatingSfOpp] = useState(false);

  // const advertiserHasContract =
  //   !!(selectedAdvertiser && selectedAdvertiser.pricingModel && selectedAdvertiser.pricingModel.hasContract === true);
  // const fourthFieldLabel = advertiserHasContract ? 'Phone Number' : 'Decision Maker Title';
  // const fourthFieldPlaceholder = advertiserHasContract
  //   ? 'Enter phone number'
  //   : 'Enter decision maker title';

  // Debounce ref for advertiser search
  const searchDebounceRef = useRef(null);

  // Bridge react-apollo@3 useLazyQuery (no awaitable) to a Promise. We register
  // a resolver here, fire the lazy query, and resolve when the hook's loading
  // flag transitions back to false.
  const proposalNumberResolverRef = useRef(null);

  useEffect(() => {
    if (
      proposalNumberResolverRef.current &&
      !loadingSalesforceOpportunityProposalNumber
    ) {
      const { resolve } = proposalNumberResolverRef.current;
      proposalNumberResolverRef.current = null;
      resolve(salesforceOpportunityProposalNumber);
    }
  }, [loadingSalesforceOpportunityProposalNumber, salesforceOpportunityProposalNumber]);

  useEffect(() => {
    if (childrenOrg && childrenOrg.length) {
      const options = childrenOrg.map((org) => ({
        value: org.id,
        label: org.name,
      }));
      // For cross-org campaigns, the selected advertiser may not be in
      // childrenOrg (e.g. parent org advertiser). Add it so the dropdown
      // displays the name instead of the raw ID.
      if (
        selectedAdvertiser &&
        !childrenOrg.some((org) => org.id === selectedAdvertiser.id)
      ) {
        options.unshift({
          value: selectedAdvertiser.id,
          label: selectedAdvertiser.name,
        });
      }
      setAdvertisers(options);
    } else {
      if (!selectedAdvertiser) return;

      setAdvertisers([
        {
          value: selectedAdvertiser.id,
          label: selectedAdvertiser.name,
        },
      ]);
    }
  }, [childrenOrg, selectedAdvertiser]);

  useEffect(() => {
    if (currentUser && currentUser.permission) {
      const authorized = currentUser.permission.isProcessor;
      setCanEdit(authorized);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUserOrg?.id) {
      handleFetchAdvertisers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserOrg?.id]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleFetchAdvertisers = useCallback((search = '') => {
    if (!currentUserOrg) return;
    fetchChildrenOrgs({
      variables: {
        where: {
          OR: [{ parentOrg_some: { id: currentUserOrg.id } }, { id: currentUserOrg.id }],
          name_contains: search,
        },
        orderBy: 'name_ASC',
        first: 1500, // TODO: Apply virtual scrolling and pagination
      },
    });
  }, [fetchChildrenOrgs, currentUserOrg]);

  // Debounced search handler for advertiser search
  const handleDebouncedAdvertiserSearch = useCallback((searchText) => {
    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new timeout (500ms delay)
    searchDebounceRef.current = setTimeout(() => {
      handleFetchAdvertisers(searchText);
    }, 500);
  }, [handleFetchAdvertisers]);

  const handleNameChange = (e) => {
    // Strip disallowed characters in real-time, then propagate every change to
    // formValues so consumers (e.g. the Create-SF-Opportunity Tooltip/disabled
    // state) see the live value instead of a stale empty string.
    const cleanValue = e.target.value.replace(/[^a-zA-Z0-9\s\-_]/g, '');
    helpers.updateField('name', cleanValue);
  };

  const resetSfOppModal = () => {
    setIsSfOppModalVisible(false);
    setSfOppName('');
    setSfBudget('');
    setSfMonths('');
    // setSfDecisionMakerFirstName('');
    // setSfDecisionMakerLastName('');
    // setSfFourthField('');
  };

  const handleCreateSalesforceOpportunity = async () => {
    if (!selectedAdvertiser) {
      message.error('No advertiser selected');
      return;
    }

    // Resolve the Salesforce AccountId for the Opportunity:
    //  - If the advertiser itself has a contract, use its salesforceId.
    //  - Otherwise, fall back to the first parentOrg with a contract.
    let resolvedAccountId = null;
    let resolvedBillingEmail = null;
    let parentOrgWithContract = null;
    if (selectedAdvertiser.pricingModel?.hasContract === true) {
      resolvedAccountId = selectedAdvertiser.salesforceId ?? null;
      resolvedBillingEmail = selectedAdvertiser.billingEmail ?? null;
    } else {
      parentOrgWithContract = (selectedAdvertiser.parentOrg || []).find(
        (org) => org?.pricingModel?.hasContract === true,
      );
      resolvedAccountId = parentOrgWithContract?.salesforceId ?? null;
      resolvedBillingEmail = parentOrgWithContract?.billingEmail ?? null;
    }

    if (!resolvedAccountId) {
      message.error(
        'No Salesforce Account found: advertiser has no contract and no parent org with a contract has a Salesforce ID',
      );
      return;
    }

    setIsCreatingSfOpp(true);

    try {
      // 1. Resolve modal values, falling back to existing form values
      const opportunityName = formValues.name;
      const opportunityAmount = formValues.budget;

      const formattedOpportunityName = formatCampaignName(
        {
          name: opportunityName,
          productCode: formValues.productCode,
          startDate: formValues.startDate,
          advertiser: selectedAdvertiser,
          type: formValues.type,
        },
        timezone,
      );

      // 2. Sync the modal values back to the form so the persisted draft
      //    reflects the user's edits.
      // helpers.updateField('name', opportunityName);
      helpers.updateField('budget', opportunityAmount);

      // 3. Save the campaign as a DRAFT first so we have a campaignOrder
      //    to attach the Salesforce Opportunity to. saveCampaign returns
      //    the campaignOrderId for both create and update flows.
      const allFormValues = helpers.form.getFieldsValue();
      const campaignData = {
        ...allFormValues,
        name: opportunityName,
        budget: opportunityAmount,
        advertiser: allFormValues.advertiser || selectedAdvertiser,
        advertiserId: allFormValues.advertiserId || selectedAdvertiser?.id,
        status: currentCampaignOrder?.status || 'DRAFT',
      };
      const isUpdate = !!currentCampaignOrder;
      const saveResult = await saveCampaign(
        campaignData,
        isUpdate,
        currentCampaignOrder,
        selectedAdvertiser,
      );
      if (!saveResult || !saveResult.success) {
        throw saveResult?.error || new Error('Failed to save campaign as draft');
      }
      const savedCampaignOrderId = saveResult.campaignOrderId;
      if (!savedCampaignOrderId) {
        throw new Error('Saved campaign order has no id');
      }

      // 4. (URL navigation is deferred until the SF flow finishes — see end of try
      //    block. Replacing the URL here triggers a `fetchCampaignOrder` race that
      //    can overwrite the cache with a stale snapshot before our IONumber write
      //    lands.)

      // 5. Build SF payloads from the resolved values.
      const isChannelPartnerOpp = selectedAdvertiser.pricingModel?.hasContract === false;

      // Create the Contact first so we can attach it to the Opportunity via
      // Deal_Signatory__c (Lookup(Contact)) on create.
      const { data: contactData } = await createSalesforceContact({
        variables: { data: { lastName: resolvedBillingEmail ?? '' } },
      });
      const contactId = contactData?.createSalesforceContact;
      if (!contactId) {
        throw new Error('Salesforce did not return a Contact Id');
      }

      // The SF line item is hardcoded to MB-DIS-DISP-ATRG ("Standard Display
      // Awareness").
      //   advertiserCPM   = cpmList[0].advertiserCPM (rack rate the advertiser
      //                     is billed at). Used to derive how many impressions
      //                     the advertiser's budget buys.
      //   channelPartnerCPM = cpmList[0].contractCPM (discounted cost basis
      //                     the CP actually pays). Multiplied by impressions
      //                     to produce the SF opp Amount.
      const sfOpportunityCampaignType = 'DISPLAY';
      const cpOrgForCpm = isChannelPartnerOpp ? parentOrgWithContract : selectedAdvertiser;
      const contractCpmEntry = cpOrgForCpm?.pricingModel?.cpmList?.[0];
      const advertiserCPM = parseInt(selectedAdvertiser.pricingModel?.cpmList[0]?.advertiserCPM);
      const channelPartnerCPM =
        contractCpmEntry?.contractCPM != null
          ? parseInt(String(contractCpmEntry.contractCPM).replace(/,/g, ''), 10)
          : 7;

      const initialImpressions = (1000 * opportunityAmount) / advertiserCPM
      // cost = CPM × impressions / 1000
      const salesforceCPBudget = channelPartnerCPM * initialImpressions / 1000

      const trimmedSfMonths = sfMonths.trim();
      const parsedSfMonths = trimmedSfMonths !== '' ? Number(trimmedSfMonths) : null;
      const lineItemQuantity =
        parsedSfMonths !== null && !Number.isNaN(parsedSfMonths) && parsedSfMonths > 0
          ? parsedSfMonths
          : 3;

      const opportunityPayload = {
        Name: formattedOpportunityName,
        StageName: 'Decision',
        CloseDate: new Date().toISOString().split('T')[0],
        AccountId: isChannelPartnerOpp
          ? selectedAdvertiser.salesforceId
          : resolvedAccountId,
        Amount: isChannelPartnerOpp ? salesforceCPBudget : opportunityAmount,
        Contract_Type__c: "Auto-Renew",
        Number_of_months__c: lineItemQuantity,
        ...(isChannelPartnerOpp && {
          LeadSource: 'Partner',
          Serviced_Account__c: resolvedAccountId,
          RecordTypeId: '0126g000000nzljAAA',
          Deal_Signatory__c: contactId,
        }),
      };

      const lineItemsPayload = {
        PricebookEntryId: 'MB-DIS-DISP-ATRG',
        Quantity: lineItemQuantity,
        UnitPrice: salesforceCPBudget / lineItemQuantity,
        Description: 'Standard Display Awareness',
        Cpm__c: isChannelPartnerOpp ? channelPartnerCPM : advertiserCPM,
        ServiceDate: formValues.startDate.format("YYYY-MM-DD"),                                                                                                                                    
        Service_End_Datee__c: formValues.endDate.format("YYYY-MM-DD"),
      };

      const { data: oppData } = await createSalesforceOpportunity({
        variables: { opportunity: opportunityPayload },
      });

      const opportunityId = oppData?.createSalesforceOpportunity;
      if (!opportunityId) {
        throw new Error('Salesforce did not return an Opportunity Id');
      }

      // 6. Persist the SF opportunityId onto the campaignOrder.
      await updateCampaignOrderSalesforceId({
        variables: {
          where: { id: savedCampaignOrderId },
          data: { salesforceId: opportunityId },
        },
      });

      const sfOppLineItems = {
        opportunityId,
        lineItems: [lineItemsPayload],
      };

      await createSalesforceOpportunityLineItems({ variables: sfOppLineItems });

      await createSalesforceOpportunityContactRole({
        variables: {
          opportunityId,
          contactId,
          roleName: 'Decision Maker',
          isPrimary: true,
        },
      });

      // react-apollo@3 useLazyQuery doesn't return a promise — fire the query
      // and await the bridge useEffect that resolves with the hook state.
      const proposalNumber = await new Promise((resolve, reject) => {
        proposalNumberResolverRef.current = { resolve, reject };
        try {
          fetchSalesforceOpportunityProposalNumber({
            variables: { salesforceOpportunityId: opportunityId },
          });
        } catch (err) {
          proposalNumberResolverRef.current = null;
          reject(err);
        }
      });

      if (!proposalNumber) {
        throw new Error('Salesforce did not return a Proposal Number');
      }

      const { data: updateData } = await updateCampaignOrder({
        variables: {
          where: { id: savedCampaignOrderId },
          data: {
            additionalIONumbers: { create: { value: proposalNumber } },
          },
        },
      });

      const updatedCampaignOrder = updateData?.updateCampaignOrderRequestor;
      if (updatedCampaignOrder?.additionalIONumbers) {
        helpers.updateField('additionalIONumbers', updatedCampaignOrder.additionalIONumbers);
      }

      // 8. Now that the SF round-trips and the IONumber write are done, update
      //    the URL so a refresh continues editing the same campaign. Doing this
      //    last avoids the race with `fetchCampaignOrder` overwriting the cache.
      if (!isUpdate) {
        history.replace(`/campaigns/requester/edit/${savedCampaignOrderId}`);
      }

      message.success(
        `Salesforce Opportunity created. Proposal Number ${proposalNumber} added to IO Numbers.`,
      );
      resetSfOppModal();
    } catch (error) {
      console.error('Error creating Salesforce Opportunity:', error);
      message.error(`Failed to create Salesforce Opportunity: ${error.message ?? error}`);
    } finally {
      setIsCreatingSfOpp(false);
    }
  };

  const handleAddNewIONumber = async () => {

    if (!newIONumber.trim()) {
      return;
    }

    // Check for duplicate IO numbers
    const currentAdditional = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];

    const isDuplicate = currentAdditional.some(
      io => io.value.toLowerCase() === newIONumber.toLowerCase()
    );

    if (isDuplicate) {
      message.warning('This IO Number already exists');
      return;
    }

    try {
      // If campaign order exists, persist to database immediately
      if (currentCampaignOrder?.id) {
        const { data } = await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              additionalIONumbers: {
                create: { value: newIONumber },
              },
            },
          },
        });

        const updatedCampaignOrder = data?.updateCampaignOrderRequestor;
        if (updatedCampaignOrder?.additionalIONumbers) {
          helpers.updateField('additionalIONumbers', updatedCampaignOrder.additionalIONumbers);
        }
        helpers.updateField('IONumber', newIONumber);
      } else {
        // For new campaigns, store in temporary form state (will be saved when campaign is created)
        const newEntry = {
          value: newIONumber,
          createdAt: new Date().toISOString(),
          __isStaged: true,
        };
        const updatedAdditional = [...currentAdditional, newEntry];

        helpers.updateField('additionalIONumbers', updatedAdditional);
        helpers.updateField('IONumber', newIONumber);
      }

      setIsModalVisible(false);
      setNewIONumber('');
    } catch (error) {
      console.error('Error adding IO number:', error);
    }
  };

  const handleDeleteIONumber = async (ioNumberId, ioNumberValue) => {
    const integrationIdsToDelete = [];
    const attachmentIdsToDelete = [];

    if (currentCampaignOrder?.externalCRMIntegrations) {
      currentCampaignOrder.externalCRMIntegrations.forEach((integration) => {
        const metadata = integration.metadata;
        const proposalNumber = metadata?.Proposal_Number__c;

        if (proposalNumber !== ioNumberValue) return;

        integrationIdsToDelete.push({ id: integration.id });

        const pandaDocRecords = metadata?.pandadoc__Panda_Doc_Documents__r?.records;
        if (!pandaDocRecords) return;

        pandaDocRecords.forEach((record) => {
          const uuid = record.pandadoc__UUID__c;
          if (!uuid) return;

          currentCampaignOrder.attachments?.forEach((attachment) => {
            if (attachment.referenceId === uuid) {
              attachmentIdsToDelete.push({ id: attachment.id });
            }
          });
        });
      });
    }

    if (!ioNumberId) {
      const currentAdditional = formValues.additionalIONumbers || [];
      const updatedAdditional = currentAdditional.filter((item) => item.value !== ioNumberValue);
      helpers.updateField('additionalIONumbers', updatedAdditional);

      if (formValues.IONumber === ioNumberValue) {
        helpers.updateField('IONumber', null);
      }
      return;
    }

    try {
      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: {
            additionalIONumbers: {
              delete: [{ id: ioNumberId }],
            },
            externalCRMIntegrations: {
              delete: integrationIdsToDelete,
            },
            attachments: {
              delete: attachmentIdsToDelete,
            },
          },
        },
      });

      const currentAdditional = formValues.additionalIONumbers || [];
      const updatedAdditional = currentAdditional.filter((item) => item.value !== ioNumberValue);
      helpers.updateField('additionalIONumbers', updatedAdditional);

      if (formValues.IONumber === ioNumberValue) {
        helpers.updateField('IONumber', null);
      }

      message.success('IO Number deleted successfully');
    } catch (error) {
      console.error(error);
      message.error('Failed to delete IO Number');
    }
  };

  const handleIONumberChange = (value) => {
    helpers.updateField('IONumber', value);
  };

  const handleAddIOFromSearch = async (value) => {
    if (!value.trim()) return;

    const currentAdditional = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];
    const isDuplicate = currentAdditional.some(
      io => io.value.toLowerCase() === value.toLowerCase()
    );
    if (isDuplicate) {
      message.warning('This IO Number already exists');
      return;
    }

    try {
      if (currentCampaignOrder?.id) {
        const { data } = await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              additionalIONumbers: {
                create: { value },
              },
            },
          },
        });

        const updatedCampaignOrder = data?.updateCampaignOrderRequestor;
        if (updatedCampaignOrder?.additionalIONumbers) {
          helpers.updateField('additionalIONumbers', updatedCampaignOrder.additionalIONumbers);
        }
        helpers.updateField('IONumber', value);
      } else {
        const newEntry = {
          value,
          createdAt: new Date().toISOString(),
          __isStaged: true,
        };
        const updatedAdditional = [...currentAdditional, newEntry];

        helpers.updateField('additionalIONumbers', updatedAdditional);
        helpers.updateField('IONumber', value);
      }

      setIOSearchText('');
    } catch (error) {
      console.error('Error adding IO number:', error);
      message.error('Failed to add IO Number');
    }
  };

  // Prepare dropdown options
  const ioNumberSource = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];

  const ioNumberOptions = [
    ...ioNumberSource.map((ioNumber, index) => ({
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{ioNumber.value}</span>
          <span
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Popconfirm
              placement="right"
              title="Are you sure you want to delete this IO Number?"
              onConfirm={() => handleDeleteIONumber(ioNumber.id, ioNumber.value)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined />
            </Popconfirm>
          </span>
        </div>
      ),
      value: ioNumber.value,
      key: index + 1,
    })),
  ];

  return (
    <>
      <Card id="requester-details" title="CAMPAIGN ORDER">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={20}>
            <Col xs={24} md={10}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Advertiser</Text>
                <Text type="secondary">The one being promoted in this campaign</Text>
                <Form.Item
                  name="advertiserId"
                  rules={[
                    {
                      required: true,
                      message: 'Please select an advertiser',
                    },
                  ]}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select Advertiser"
                    optionFilterProp="children"
                    loading={loadingChildrenOrgs}
                    onSearch={handleDebouncedAdvertiserSearch}
                    filterOption={(input, option) => {
                      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    options={advertisers}
                    disabled={!canEdit}
                    value={formValues.advertiserId}
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col xs={24} md={14}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Campaign Name</Text>
                <Text type="secondary">The name you will identify this campaign by</Text>
                <Form.Item
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter campaign name',
                    },
                    {
                      pattern: /^[a-zA-Z0-9\s\-_]*$/,
                      message:
                        'Only letters, numbers, spaces, hyphens, and underscores are allowed',
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter campaign name"
                    value={formValues.name}
                    onChange={handleNameChange}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Description</Text>
                <Text type="secondary">Optional: Helps to distinguish your campaign</Text>
                <Form.Item name="description">
                  <Input
                    placeholder="Enter optional description for campaign"
                    value={formValues.description}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>IO Number</Text>
                <Text type="secondary">IO number or account identifier</Text>
                <Form.Item
                  name="IONumber"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter IO Number',
                    },
                  ]}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select or type new IO Number"
                    value={formValues.IONumber}
                    onChange={handleIONumberChange}
                    options={ioNumberOptions}
                    searchValue={ioSearchText}
                    onSearch={setIOSearchText}
                    onSelect={() => setIOSearchText('')}
                    filterOption={(input, option) =>
                      option.value.toLowerCase().includes(input.toLowerCase())
                    }
                    dropdownRender={(menu) => {
                      const trimmedSearch = ioSearchText.trim();
                      const exactMatchExists = ioNumberSource.some(
                        io => io.value.toLowerCase() === trimmedSearch.toLowerCase()
                      );
                      const showAddOption = trimmedSearch && !exactMatchExists;

                      return (
                        <>
                          {menu}
                          <Divider style={{ margin: '8px 0' }} />
                          {showAddOption ? (
                            <div
                              style={{ padding: '0 8px 4px', cursor: 'pointer', color: '#1890ff' }}
                              onClick={() => handleAddIOFromSearch(trimmedSearch)}
                            >
                              <PlusOutlined /> Add "{trimmedSearch}" as IO Number
                            </div>
                          ) : (
                            <div
                              style={{ padding: '0 8px 4px', cursor: 'pointer', color: '#1890ff' }}
                              onClick={() => setIsModalVisible(true)}
                            >
                              <PlusOutlined /> Add New IO Number
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Salesforce</Text>
                <Text type="secondary">Create a new opportunity in Salesforce</Text>
                <Tooltip
                  title={
                    !formValues.name
                      ? 'Please fill in the Campaign Name before creating a Salesforce Opportunity'
                      : !formValues.budget
                        ? 'Please fill in the Campaign Budget before creating a Salesforce Opportunity'
                        : ''
                  }
                >
                  <span style={{ display: 'inline-block' }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setSfOppName(formValues.name ?? '');
                        setSfBudget(formValues.budget != null ? String(formValues.budget) : '');
                        setIsSfOppModalVisible(true);
                      }}
                      disabled={!selectedAdvertiser || !formValues.budget || !formValues.name}
                    >
                      Create Salesforce Opportunity
                    </Button>
                  </span>
                </Tooltip>
              </Space>
            </Col>
            <HasAccess roles={['isTrafficker']}>
              <Col span={8}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text>Renewal Status</Text>
                  <Text type="secondary">Renewal status of the campaign</Text>
                  <Form.Item name="renewalStatus">
                    <Select
                      style={{ width: '100%' }}
                      showSearch
                      placeholder="Expected to Renew"
                      value={formValues.renewalStatus}
                      options={[
                        {
                          label: 'Expected to Renew',
                          value: 'EXPECTED_TO_RENEW',
                          key: 1,
                        },
                        {
                          label: 'Not Renewing',
                          value: 'NOT_RENEWING',
                          key: 2,
                        },
                      ]}
                    />
                  </Form.Item>
                </Space>
              </Col>
            </HasAccess>
          </Row>
        </Space>
      </Card>
      <Form.Item name="productCode" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="additionalIONumbers" hidden>
        <Input />
      </Form.Item>

      <Modal
        title="Add New IO Number"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsModalVisible(false)}>
            Back
          </Button>,
          <Button key="submit" type="primary" onClick={handleAddNewIONumber}>
            Confirm
          </Button>,
        ]}
      >
        <Input
          placeholder="Enter IO Number"
          value={newIONumber}
          onChange={(e) => setNewIONumber(e.target.value)}
        />
      </Modal>

      <Modal
        title="Create Salesforce Opportunity"
        open={isSfOppModalVisible}
        onCancel={() => {
          if (isCreatingSfOpp) return;
          resetSfOppModal();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={resetSfOppModal}
            disabled={isCreatingSfOpp}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleCreateSalesforceOpportunity}
            loading={
              isCreatingSfOpp ||
              loadingCreateSalesforceOpportunity ||
              loadingCreateSalesforceOpportunityLineItems ||
              loadingSalesforceOpportunityProposalNumber
            }
          >
            Confirm
          </Button>,
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* <div>
            <Text>Opportunity Name</Text>
            <Input
              value={sfOppName}
              onChange={(e) => setSfOppName(e.target.value)}
            />
          </div> */}
          {/* <div>
            <Text>Campaign Budget</Text>
            <Input
              type="number"
              value={sfBudget}
              onChange={(e) => setSfBudget(e.target.value)}
            />
          </div> */}
          <div>
            <Text>Months</Text>
            <Input
              type="number"
              min={1}
              placeholder="Defaults to 3 if left empty"
              value={sfMonths}
              onChange={(e) => setSfMonths(e.target.value)}
            />
          </div>
          {/* <div>
            <Text>Decision Maker First Name</Text>
            <Input
              placeholder="Enter decision maker first name"
              value={sfDecisionMakerFirstName}
              onChange={(e) => setSfDecisionMakerFirstName(e.target.value)}
            />
          </div>
          <div>
            <Text>Decision Maker Last Name</Text>
            <Input
              placeholder="Enter decision maker last name"
              value={sfDecisionMakerLastName}
              onChange={(e) => setSfDecisionMakerLastName(e.target.value)}
            />
          </div>
          <div>
            <Text>{fourthFieldLabel}</Text>
            <Input
              placeholder={fourthFieldPlaceholder}
              value={sfFourthField}
              onChange={(e) => setSfFourthField(e.target.value)}
            />
          </div> */}
        </Space>
      </Modal>
    </>
  );
};

export default CampaignRequestorDetails;